import React, { useState, useEffect, useMemo } from 'react';
// import { useNavigate } from 'react-router-dom'; // Kommentera bort tills det är installerat/använt
import { login, LoginCredentials, changePassword, logout as authLogout, consumeLogoutReason } from '../services/authService';
import { Box, TextField, Button, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Link, CircularProgress, Divider } from '@mui/material';
import { Email as EmailIcon, Phone as PhoneIcon, SupportAgent as SupportIcon, Send as SendIcon } from '@mui/icons-material';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import ResetPasswordPage from './ResetPasswordPage';

interface LoginPageProps {
  onLoginSuccess: (user: any) => void; // Typen för user kan förfinas senare
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  // const navigate = useNavigate(); // För react-router-dom
  const [credentials, setCredentials] = useState<LoginCredentials>({ email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(false);
  const [cpCurrent, setCpCurrent] = useState<string>('');
  const [cpNew, setCpNew] = useState<string>('');
  const [cpConfirm, setCpConfirm] = useState<string>('');
  const [cpError, setCpError] = useState<string | null>(null);
  const [cpLoading, setCpLoading] = useState<boolean>(false);
  const [loginSettings, setLoginSettings] = useState<{ logoPath: string; loginImagePath: string; topLogoPath?: string; bottomLogoPath?: string; loginText?: string; topLogoHeight?: number }>({ logoPath: '', loginImagePath: '', topLogoPath: '', bottomLogoPath: '', loginText: '', topLogoHeight: 100 });
  
  // Forgot password dialog state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Hämta login-inställningar
  useEffect(() => {
    const fetchLoginSettings = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/login-settings`);
        setLoginSettings(response.data);
      } catch (error) {
        console.error('Error fetching login settings:', error);
        // Använd default-inställningar om hämtning misslyckas
        setLoginSettings({ logoPath: '', loginImagePath: '', topLogoPath: '', bottomLogoPath: '', loginText: '', topLogoHeight: 100 });
      }
    };
    fetchLoginSettings();
  }, []);

  useEffect(() => {
    const reason = consumeLogoutReason();
    if (reason === 'idle_timeout') {
      setInfoMessage('Du har loggats ut efter 3 timmars inaktivitet.');
    } else if (reason === 'password_changed') {
      setInfoMessage('Ditt lösenord har ändrats. Logga in igen.');
    } else if (reason === 'session_invalid') {
      setInfoMessage('Din session är inte längre giltig. Logga in igen.');
    }
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials({ ...credentials, [event.target.name]: event.target.value });
  };

  // Reset password route detection (handles both /reset-password and ?token=)
  const isResetPasswordRoute = useMemo(() => {
    const pathname = window.location.pathname;
    const search = window.location.search;
    return pathname.includes('reset-password') || (search && search.includes('token='));
  }, []);

  if (isResetPasswordRoute) {
    return (
      <ResetPasswordPage
        onBackToLogin={() => {
          window.history.replaceState(null, '', '/');
          window.location.reload();
        }}
      />
    );
  }

  // Handle forgot password request
  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setResetError('Ange din e-postadress');
      return;
    }
    
    setResetLoading(true);
    setResetError(null);
    
    try {
      await axios.post(`${API_BASE_URL}/api/auth/forgot-password`, { email: resetEmail });
      setResetSuccess(true);
    } catch (err: any) {
      setResetError(err.response?.data?.message || 'Ett fel uppstod. Försök igen.');
    } finally {
      setResetLoading(false);
    }
  };

  const closeForgotPasswordDialog = () => {
    setForgotPasswordOpen(false);
    setResetEmail('');
    setResetSuccess(false);
    setResetError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setInfoMessage(null);
    try {
      const response = await login(credentials);
      if (response.data && response.data.token && response.data.user) {
        if ((response.data as any).mustChangePassword) {
          setMustChangePassword(true);
          setCpCurrent('');
          setCpNew('');
          setCpConfirm('');
        } else {
          onLoginSuccess(response.data.user);
          // navigate('/'); // Omdirigera till huvudsidan med react-router-dom
        }
      } else {
        setError(response.error || 'Inloggning misslyckades. Kontrollera dina uppgifter.');
      }
    } catch (err: any) {
      setError(err.message || 'Ett oväntat fel inträffade.');
    }
    setLoading(false);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      backgroundColor: '#0b2441',
      alignItems: 'stretch'
    }}>
      {/* Vänster sida - Inloggningsformulär */}
      <Box sx={{ 
        flex: { xs: '1', md: '0 0 50%' },
        maxWidth: { md: '800px' },
        minWidth: { md: '500px' },
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#072f53',
        color: 'white',
        p: { xs: 3, md: 6 },
        position: 'relative',
        overflow: 'auto'
      }}>
        {/* Wrapper för att centrera innehållet vertikalt */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center',
          flex: 1,
          minHeight: 0
        }}>
          {/* Övre logotyp */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 3, md: 4 } }}>
            <img
              src={(loginSettings.topLogoPath || loginSettings.logoPath || '/logo.png') as string}
              alt="Top Logo"
              style={{ 
                height: `${loginSettings.topLogoHeight || 100}px`,
                width: 'auto',
                objectFit: 'contain',
                maxWidth: '100%'
              }}
            />
          </Box>

          {/* Center-innehåll - formulär */}
          <Box sx={{ width: '100%', maxWidth: '500px', mx: 'auto' }}>
            {/* Infotext */}
            {loginSettings.loginText ? (
              <Box sx={{ mb: 3, lineHeight: 1.6, textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(loginSettings.loginText || '')) }} />
            ) : null}

            {/* Formulär */}
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="E-postadress"
                name="email"
                autoComplete="email"
                autoFocus
                value={credentials.email}
                onChange={handleChange}
                disabled={loading}
                InputLabelProps={{
                  sx: {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&.Mui-focused': { color: '#072f53' },
                    '&.MuiFormLabel-filled': { color: '#072f53' }
                  }
                }}
                sx={{ 
                  backgroundColor: 'white', 
                  borderRadius: 1,
                  '& .MuiInputLabel-shrink': { 
                    color: '#072f53',
                    backgroundColor: 'white',
                    px: 0.5
                  }
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Lösenord"
                type="password"
                id="password"
                autoComplete="current-password"
                value={credentials.password}
                onChange={handleChange}
                disabled={loading}
                InputLabelProps={{
                  sx: {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&.Mui-focused': { color: '#072f53' },
                    '&.MuiFormLabel-filled': { color: '#072f53' }
                  }
                }}
                sx={{ 
                  backgroundColor: 'white', 
                  borderRadius: 1,
                  '& .MuiInputLabel-shrink': { 
                    color: '#072f53',
                    backgroundColor: 'white',
                    px: 0.5
                  }
                }}
              />
              {error && (
                <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                  {error}
                </Alert>
              )}
              {infoMessage && (
                <Alert severity="info" sx={{ mt: 2, width: '100%' }}>
                  {infoMessage}
                </Alert>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ 
                  mt: 3,
                  mb: 2,
                  backgroundColor: 'white',
                  color: '#072f53',
                  '&:hover': {
                    backgroundColor: '#f0f0f0'
                  },
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  py: 1.5
                }}
                disabled={loading}
              >
                {loading ? 'Loggar in...' : 'Logga in'}
              </Button>
              
              {/* Glömt lösenord-länk */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setForgotPasswordOpen(true);
                  }}
                  sx={{ 
                    color: 'white',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Glömt ditt lösenord?
                </Link>
              </Box>
            </Box>
          </Box>

          {/* Nedre logotyp */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, md: 6 } }}>
            <img
              src={(loginSettings.bottomLogoPath || '/Fontana_Logo_Neg.png') as string}
              alt="Bottom Logo"
              style={{ maxHeight: 60, width: 'auto', objectFit: 'contain', opacity: 0.95, maxWidth: '100%' }}
            />
          </Box>
        </Box>
      </Box>

      {/* Höger sida - Bild */}
      <Box sx={{ 
        flex: { xs: '0', md: '1' },
        display: { xs: 'none', md: 'block' },
        position: 'relative',
        minHeight: { md: '100vh' },
        overflow: 'hidden'
      }}>
        {loginSettings.loginImagePath ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              backgroundImage: `url(${loginSettings.loginImagePath})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        ) : (
          <Box sx={{ 
            width: '100%', 
            height: '100%', 
            backgroundColor: '#e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="h6" color="text.secondary">
              Ladda upp en bild i admin-inställningar
            </Typography>
          </Box>
        )}
      </Box>

      {/* Dialog för lösenordsbyte vid första inloggning */}
      <Dialog open={mustChangePassword}>
        <DialogTitle>Byt lösenord</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Av säkerhetsskäl behöver du byta ditt initiala lösenord innan du kan fortsätta.
          </Typography>
          <TextField
            margin="normal"
            fullWidth
            label="Nuvarande lösenord"
            type="password"
            value={cpCurrent}
            onChange={(e) => setCpCurrent(e.target.value)}
            disabled={cpLoading}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Nytt lösenord"
            type="password"
            value={cpNew}
            onChange={(e) => setCpNew(e.target.value)}
            disabled={cpLoading}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Bekräfta nytt lösenord"
            type="password"
            value={cpConfirm}
            onChange={(e) => setCpConfirm(e.target.value)}
            disabled={cpLoading}
          />
          {cpError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {cpError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                setCpError(null);
                if (!cpCurrent || !cpNew) {
                  setCpError('Fyll i alla fält.');
                  return;
                }
                if (cpNew !== cpConfirm) {
                  setCpError('Det nya lösenordet och bekräftelsen matchar inte.');
                  return;
                }
                setCpLoading(true);
                const resp = await changePassword(cpCurrent, cpNew);
                if (resp.error) {
                  setCpError(resp.error || 'Kunde inte byta lösenord.');
                } else {
                  await authLogout({ server: false, reason: 'password_changed' });
                  setMustChangePassword(false);
                  setInfoMessage('Lösenordet har ändrats. Logga in igen med ditt nya lösenord.');
                }
              } catch (err: any) {
                setCpError(err?.message || 'Ett fel inträffade.');
              } finally {
                setCpLoading(false);
              }
            }}
            disabled={cpLoading}
          >
            {cpLoading ? 'Byter...' : 'Byt lösenord'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Glömt lösenord dialog */}
      <Dialog 
        open={forgotPasswordOpen} 
        onClose={closeForgotPasswordDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupportIcon color="primary" />
            <Typography variant="h6">Behöver du hjälp?</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {!resetSuccess ? (
            <>
              {/* Kontaktinfo-sektion */}
              <Box sx={{ 
                bgcolor: '#f5f8ff', 
                p: 3, 
                borderRadius: 2, 
                mb: 3,
                border: '1px solid #e0e7ff'
              }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: '#072f53' }}>
                  💬 Kontakta oss direkt
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Vi hjälper dig gärna! Kontakta Olle eller Franco så fixar vi det snabbt.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: '#072f53' }}>
                  Vi finns tillgängliga under kontorstid och svarar så fort vi kan! 😊
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }}>
                <Typography variant="caption" color="text.secondary">eller</Typography>
              </Divider>

              {/* Email reset sektion */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" color="primary" />
                  Återställ via e-post
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Ange din e-postadress så skickar vi en länk för att återställa lösenordet.
                </Typography>
                
                <TextField
                  fullWidth
                  type="email"
                  label="E-postadress"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  disabled={resetLoading}
                  size="small"
                  sx={{ mb: 2 }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleForgotPassword();
                    }
                  }}
                />

                {resetError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {resetError}
                  </Alert>
                )}

                <Button
                  variant="contained"
                  onClick={handleForgotPassword}
                  disabled={resetLoading || !resetEmail}
                  startIcon={resetLoading ? <CircularProgress size={20} /> : <SendIcon />}
                  fullWidth
                >
                  {resetLoading ? 'Skickar...' : 'Skicka återställningslänk'}
                </Button>
              </Box>
            </>
          ) : (
            /* Framgångsmeddelande */
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Box sx={{ 
                width: 64, 
                height: 64, 
                borderRadius: '50%', 
                bgcolor: '#e8f5e9', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <EmailIcon sx={{ fontSize: 32, color: '#4caf50' }} />
              </Box>
              <Typography variant="h6" gutterBottom>
                Kolla din inkorg! 📬
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Om e-postadressen finns i vårt system har vi skickat en återställningslänk.
                Glöm inte att kolla skräpposten!
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                Länken är giltig i 1 timme.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeForgotPasswordDialog}>
            {resetSuccess ? 'Stäng' : 'Avbryt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginPage; 